<template>
    <div>
        <slot></slot>
        <b-row>
            <b-col v-if="purchaseOrder.generatedFromWantList">
                <span>
                    Generated from wantlist:
                    <b-link
                        @click.prevent="
                            loadWantList(
                                purchaseOrder.generatedFromWantList.wantListId
                            )
                        "
                        >{{ purchaseOrder.generatedFromWantList.name }}</b-link
                    >
                </span>
            </b-col>
            <b-col cols="12" class="d-flex">
                <ProductMultiSelectQuickSearch
                    :vendor-id="purchaseOrder.supplierId"
                    :is-busy="isBusy"
                    no-packages
                    is-active-filter
                    include-terms
                    label="Add Product(s)"
                    icon-plus
                    name="itemSelectTop"
                    @selections="addSelectedItem"
                    fieldSet="poFields"
                    dialog-class="po-add-products-modal"
                    :per-page="perPageAddProduct"
                    @per-page-change="e => {perPageAddProduct = e}"
                    :disable-add-button="!purchaseOrder.supplierId"
                />
                <b-overlay :show='isExporting || isBusy'
                                rounded
                                opacity='0.5'
                                spinner-small
                                spinner-variant='primary'
                                class='d-inline-block ms-2'>
                    <b-dropdown
                        text='Export'
                        variant="outline-primary"
                    >
                        <b-dropdown-item
                            @click='showExportPurchaseOrder()'
                            :disabled="statusClosed || isNew"
                            >Export PO (PDF)</b-dropdown-item
                        >
                        <b-dropdown-item
                            @click='exportPurchaseOrder("csv")'
                            :disabled="statusClosed || isNew"
                            >Export PO (CSV)</b-dropdown-item
                        >
                        <b-dropdown-item
                            @click='exportReceivingDocument("pdf")'
                            :disabled="!statusOpen || isNew"
                            >Export Receiving Document</b-dropdown-item
                        >
                    </b-dropdown>
                </b-overlay>
                <b-overlay :show='isExporting || isBusy'
                                rounded
                                opacity='0.5'
                                spinner-small
                                spinner-variant='primary'
                                class='d-inline-block ms-2 me-2'>
                    <b-dropdown
                        text='Print Labels'                        
                        variant="outline-primary"
                        :disabled="statusProposed || isNew">
                        <b-dropdown-item
                            @click='printLabels(false)'>
                            Print 1 of Each
                        </b-dropdown-item>
                        <b-dropdown-item
                            @click='printLabels(true)'
                            :disabled="!hasReceivedQty">
                            Print All Rcv Qty
                        </b-dropdown-item>
                    </b-dropdown>
                </b-overlay>
                <p-button
                    class="mb-2 me-2"
                    variant="outline-primary"
                    @click="viewSupplierDiscounts"
                    v-b-modal.ViewSupplierDiscounts
                    :disabled="!purchaseOrder.supplierId"
                    :is-busy="isBusy"
                    >View Supplier Discounts</p-button
                >
                <p-button
                    :disabled="isNew"
                    class="mb-2"
                    variant="outline-danger"
                    @click="$emit('deletePurchaseOrder')"
                    :is-busy="isBusy"
                    >Delete PO</p-button
                >
                <p-button
                    variant="primary"
                    class="mb-2 ms-auto"
                    :is-busy="isBusy"
                    @click="onSave">
                    Save
                </p-button>
            </b-col>
        </b-row>
        <span id='tagContent' v-html="tagContent" />
        <p-form ref="purchaseOrderForm" id="purchaseOrder-form">
            <p-card>
                <b-row cols="1" cols-sm="2" cols-xl="4">
                    <b-col>
                        <p-select
                            data-type="poStatus"
                            :model-value="purchaseOrder.status"
                            label="Status"
                            text-field="value"
                            rule="required"
                            @input="changePOStatus($event)"
                            :key="purchaseOrder.status + validationCheck"
                        />
                    </b-col>
                    <b-col>
                        <p-input
                            name="ponumber"
                            v-model="purchaseOrder.poNumber"
                            label="PO Number (generated on save if left blank)"
                            placeholder="Generated On Save"
                            :disabled="!statusProposed"
                            :state="duplicatePoNumber ? false : null"
                            ref="poNumberInput"
                            @blur="isPurchaseOrderNumberInUse()"
                        />
                    </b-col>
                    <b-col>
                        <p-datepicker
                            @date-picker-initialized="e => {$emit('date-picker-initialized');}"
                            v-model="purchaseOrder.purchaseOrderDate"
                            label="PO Date"
                            :disabled="statusClosed || statusOpen"
                        />
                    </b-col>
                    <b-col>
                        <p-number
                            name="total"
                            input-type="money"
                            :model-value="total"
                            label="Total"
                            :disabled="true"
                            :add-money-rules="false"
                        />
                    </b-col>

                    <b-col>
                        <p-advanced-select
                            dataType="suppliers"
                            v-model="purchaseOrder.supplierId"
                            label="Supplier"
                            rules="required"
                            :disabled="statusClosed || statusOpen"
                            @change="supplierChanged(purchaseOrder.supplierId)"
                        />
                    </b-col>
                    <b-col>
                        <p-datepicker
                            @date-picker-initialized="e => {$emit('date-picker-initialized');}"
                            v-model="purchaseOrder.expectedArrival"
                            label="Expected Arrival"
                            :disabled="statusClosed"
                        />
                    </b-col>
                    <b-col>
                        <p-datepicker
                            @date-picker-initialized="e => {$emit('date-picker-initialized');}"
                            v-model="purchaseOrder.shipByDate"
                            label="Ship By"
                            :disabled="statusClosed"
                        />
                    </b-col>
                    <b-col>
                        <p-datepicker
                            @date-picker-initialized="e => {$emit('date-picker-initialized');}"
                            v-model="purchaseOrder.cancelAfterDate"
                            label="Cancel After"
                            :disabled="statusClosed"
                        />
                    </b-col>
                    <b-col>
                        <p-input
                            name="Account Number"
                            label="Account Number"
                            v-model="purchaseOrder.supplier.accountNumber"
                            disabled
                        />
                    </b-col>
                    <b-col>
                        <p-select
                            dataType="shippingProviders"
                            v-model="purchaseOrder.shippingProviderId"
                            label="Ship By Preference"
                            :disabled="statusClosed"
                            add-empty-option
                        />
                    </b-col>
                    <b-col>
                        <p-select
                            dataType="shippingLevels"
                            v-model="purchaseOrder.shippingLevelId"
                            label="Ship By Service Level"
                            :disabled="statusClosed"
                            add-empty-option
                        />
                    </b-col>
                    <b-col>
                        <p-input
                            name="trackingNumber"
                            v-model="purchaseOrder.trackingNumber"
                            label="Tracking Number"
                            rules="max:50"
                            :disabled="!statusOpen"
                        />
                    </b-col>
                    <b-col>
                        <p-input
                            name="Phone Number"
                            label="Phone Number"
                            v-model="purchaseOrder.supplier.phoneNumber"
                            disabled
                        />
                    </b-col>
                    <b-col>
                        <p-advanced-select
                            name="buyerName"
                            data-type="users"
                            v-model="purchaseOrder.buyerId"
                            label="Buyer"
                            :disabled="statusClosed || statusOpen"
                        />
                    </b-col>
                    <b-col>
                        <p-number
                            v-model="purchaseOrder.discountAmount"
                            input-type="money"
                            rules="min_value:0"
                            label="Discount Amount"
                            :disabled="statusClosed || statusOpen"
                            @blur="
                                amountDollarCheck(purchaseOrder.discountAmount)
                            "
                        />
                    </b-col>
                    <b-col>
                        <p-number
                            v-model="purchaseOrder.discountPercent"
                            input-type="percent"
                            rules="p-decimal:5,2|min_value:0|max_value:100"
                            label="Discount Percentage"
                            :disabled="statusClosed || statusOpen"
                            @blur="
                                amountDiscountCheck(
                                    purchaseOrder.discountPercent
                                )
                            "
                        />
                    </b-col>
                    <b-col>
                        <p-input
                            name="Fax Number"
                            label="Fax Number"
                            v-model="purchaseOrder.supplier.faxNumber"
                            disabled
                        />
                    </b-col>
                    <b-col>
                        <p-input
                            name="referencenumber"
                            v-model="purchaseOrder.referenceNumber"
                            label="Reference Number"
                            rules="max:50"
                            :disabled="statusClosed"
                        />
                    </b-col>
                    <b-col>
                        <p-address-input
                            same-as-address-name="Delivery"
                            label="Store Billing Address"
                            :key="purchaseOrder.purchaseOrderId"
                            v-model="purchaseOrder.storeBillingAddress"
                            :same-as-address="
                                purchaseOrder.storeShippingAddress
                            "
                            :disabled="statusClosed || statusOpen"
                        />
                    </b-col>
                    <b-col>
                        <p-address-input
                            :key="purchaseOrder.purchaseOrderId"
                            label="Store Delivery Address"
                            v-model="purchaseOrder.storeShippingAddress"
                            :disabled="statusClosed || statusOpen"
                        />
                    </b-col>

                </b-row>
                <b-row>
                    <b-col cols="6" lg="3">
                        <p-input
                            name="Email Address"
                            v-model="purchaseOrder.supplier.email"
                            label="Email Address"
                            disabled
                        />
                    </b-col>
                    <b-col cols="6" lg="3">
                        <p-input
                            name="External PO Number"
                            v-model="purchaseOrder.externalPONumber"
                            label="External PO Number"
                            rules="max:25"
                            :disabled="statusClosed"
                        />
                    </b-col>                    
                    <b-col cols="12" lg="6" class="mt-3">
                        <label class="d-block">
                            Website Address:
                        </label>
                            <b-link
                                :href="'//'+purchaseOrder.supplier.website"
                                target="_blank"
                                >{{purchaseOrder.supplier.website}}</b-link>
                    </b-col>
                </b-row>
                <b-row cols="1">
                    <b-col>
                    <p-text-area
                            class='px-3'
                            label='Comments'
                            rules='max:2000'
                            v-model="purchaseOrder.comment" />
                    </b-col>
                </b-row>
            </p-card>
            <p-card>
                <b-row>
                    <b-col cols="12" class="text-start">
                        <p-form inline v-if="!statusClosed" class="d-flex align-items-center flex-wrap">
                            <ProductMultiSelectQuickSearch
                                :vendor-id="purchaseOrder.supplierId"
                                :is-busy="isBusy"
                                no-packages
                                is-active-filter
                                include-terms
                                label="Add Product(s)"
                                icon-plus
                                name="itemSelect"
                                @selections="addSelectedItem"
                                fieldSet="poFields"
                                dialog-class="po-add-products-modal"
                                :per-page="perPageAddProduct"
                                @per-page-change="e => {perPageAddProduct = e}"
                                :disable-add-button="!purchaseOrder.supplierId"
                            />
                            <p-button
                                variant="outline-primary"
                                :is-busy="isBusy"
                                v-if="!statusProposed"
                                class="ms-4"
                                @click="batchReceive()"
                                >Batch Receive</p-button
                            >
                            <p-button
                                variant="outline-primary"
                                :is-busy="isBusy"
                                class="ms-2"
                                v-if="!statusProposed"
                                @click="batchCancel()"
                                >Batch Cancel</p-button
                            >
                            <div class="ms-auto mt-2 d-flex align-items-center">
                                <span class="me-2">Update Selling Price(s)?</span>
                                <p-checkbox
                                    use-switch
                                    inline
                                    :disabled="statusProposed"
                                    v-model="purchaseOrder.updateSkuRetailPrices">
                                    <template v-slot:append>
                                        {{ purchaseOrder.updateSkuRetailPrices ? 'Yes' : 'No' }}
                                    </template>
                                </p-checkbox>
                            </div>
                        </p-form>
                    </b-col>
                </b-row>
                <b-row class="mt-2">
                    <b-col cols="12">
                        <b-overlay :show="!afterLoad"
                                    rounded
                                    opacity='0.5'
                                    spinner-variant='primary'
                                    >
                            <p-table
                                :items="purchaseOrder.purchaseOrderDetails"
                                :fields="getFields"
                                :current-page="currentPage"
                                :sort-by= filters.sortBy
                                :sort-desc=filters.sortDesc
                                :enable-row-selection="statusProposed"
                                :selection-actions="[{
                                    label: 'Delete',
                                    variant: 'outline-danger',
                                    action: this.removeProducts,
                                    hidden: !statusProposed
                                }]"
                                 :per-page="perPage"
                                v-show='afterLoad'
                                @sort-by-change="sortByUpdate"
                                @sort-desc-change="sortDescUpdate"
                                >
                                    <template
                                        v-if="!statusClosed"
                                        v-slot:head(cost)="data"
                                    >
                                        <b-form-radio
                                            :value="0"
                                            v-model="calculationOption"
                                            v-b-tooltip.hover.left
                                            title="Select To Calculate"
                                            >Cost</b-form-radio
                                        >
                                    </template>
                                    <template
                                        v-if="!statusClosed"
                                        v-slot:head(margin)="data"
                                    >
                                        <b-form-radio
                                            :value="1"
                                            v-model="calculationOption"
                                            v-b-tooltip.hover.left
                                            title="Select To Calculate"
                                            >Margin</b-form-radio
                                        >
                                    </template>
                                    <template
                                        v-if="!statusClosed"
                                        v-slot:head(retail)="data"
                                    >
                                        <b-form-radio
                                            :value="2"
                                            v-model="calculationOption"
                                            v-b-tooltip.hover.left
                                            title="Select To Calculate"
                                            >Retail</b-form-radio
                                        >
                                    </template>
                                    <template v-slot:cell(qty)="{ value, item }">
                                        <p-number
                                            v-model.number="item.qty"
                                            compact-format
                                            input-type="integer"
                                            name="Qty"
                                            rules="required|min_value:0"
                                            :disabled="statusClosed"
                                        />
                                    </template>

                                    <template v-slot:cell(rcv)="{ value, item }">
                                        <p-number
                                            v-model.number="item.receivedQty"
                                            compact-format
                                            input-type="integer"
                                            name="Rcv"
                                            rules="min_value:0"
                                            :disabled="statusClosed"
                                            @change="dissallowEmpty(item)"
                                        />
                                    </template>
                                    <template
                                        v-slot:cell(cancelled)="{ value, item }"
                                        v-if="!statusProposed"
                                    >
                                        <p-number
                                            v-model.number="item.cancelQty"
                                            compact-format
                                            input-type="integer"
                                            name="Cancelled"
                                            rules="min_value:0"
                                            :disabled="statusClosed"
                                            @change="dissallowEmpty(item)"
                                        />
                                    </template>
                                     <template
                                        v-slot:cell(backOrdered)="{ value, item }"
                                        v-if="!statusProposed"
                                    >
                                        <p-number
                                            v-model.number="item.backOrderQty"
                                            compact-format
                                            input-type="integer"
                                            name="Back Ordered"
                                            :rules="getBackorderRules(item)"
                                            :disabled="statusClosed"
                                            @change="dissallowEmpty(item)"
                                        />
                                    </template>
                                    <template v-slot:cell(margin)="{ value, item }">
                                        <p-number
                                            v-model="item.margin"
                                            compact-format
                                            @input="updateCalculation(item)"
                                            style="width: 80px;"
                                            name="Margin"
                                            input-type="percent"
                                            rules="required|max_value:99.99"
                                            :disabled="
                                                statusClosed || calculationOption === 1
                                            "
                                        />
                                    </template>
                                <template v-slot:cell(cost)="{ value, item }">
                                    <p-number
                                        v-model="item.cost"
                                        compact-format
                                        style="width: 80px;"
                                        @input="updateCalculation(item)"
                                        name="Cost"
                                        input-type="money"
                                        rules="required|min_value:0"
                                        :disabled="
                                            statusClosed || calculationOption === 0
                                        "
                                    />
                                </template>
                                <template v-slot:cell(retail)="{ value, item }">
                                    <p-number
                                        v-model="item.retail"
                                        compact-format
                                        @input="updateCalculation(item)"
                                        style="width: 80px;"
                                        name="Retail"
                                        input-type="money"
                                        rules="required|min_value:0"
                                        :disabled="statusClosed || calculationOption === 2"
                                    />
                                </template>
                                <template>
                                </template>                                
                                <template v-slot:cell(delete)="{ value, item }">
                                    <b-button
                                        v-if="!statusClosed && !item.receivedQty"
                                        type="button"
                                        class="p-1 ms-1 short-button"
                                        @click="deleteDetail(item)"
                                    >
                                        <BIconTrash icon="trash"></BIconTrash>
                                    </b-button>
                                </template>
                            </p-table>
                        </b-overlay>
                    </b-col>
                </b-row>
            </p-card>
        </p-form>
        <p-modal
            name="ViewSupplierDiscounts"
            label="Supplier Discounts"
            ref="ViewSupplierDiscounts"
            size="md"
            ok-only
            ok-title="Close"
        >
            <template v-slot:default>
                <b-row>
                    <b-col>
                        <p-table
                            :items="SupplierDiscounts"
                            :fields="SupplierDiscountsFields"
                            :enableRowSelection="false"
                        />
                    </b-col>
                </b-row>
            </template>
        </p-modal>
        <b-overlay :show='isExporting || isBusy'
            rounded
            opacity='0.5'
            spinner-small
            spinner-variant='primary'
            class='d-inline-block ml-2 mr-2'>        
            <p-modal
                @ok="exportPurchaseOrder('pdf')"
                size="sm"
                button-label="Export PO (PDF)"
                :disable-button="statusClosed || isNew"
                label="Export PO"
                ref="exportPurchaseOrderModal"
                name="exportPurchaseOrderModal">
                <template v-slot:default>
                    <p-checkbox label="Include Retail" v-model="includeRetail" />
                </template>
            </p-modal>
        </b-overlay>
    </div>
</template>

<script>
import axios from 'axios';
import ProductMultiSelectQuickSearch from '@/components/ProductMultiSelectQuickSearch.vue';
import {
    currencyFormatter,
} from '../../components/Common/Formatters.js';
import unionBy from 'lodash/unionBy';
import pricing from '@/services/PricingCalculations.js';
// BIconTrash - use <i class="bi bi-trash"> or font-awesome icon instead
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import printJS from 'print-js';

export default {
    name: 'PurchaseOrder',
    props: {
        purchaseOrder: Object,
        isNew: {
            type: Boolean,
            value: false
        },
        isBusy:  {
            type: Boolean,
            value: false
        },
        formIsDirty: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            currentPage: 1,
            calculationOption: 1,
            validationCheck: 0,
            perPage: 500,
            perPageAddProduct: 100,
            afterLoad: false,
            isExporting: false,
            includeRetail: true,
            duplicatePoNumber: false,
            tagContent: null,
            filters: {
                sortBy: 'authorTitleOrDescription',
                sortDesc: true,
            },
            // details grid
             fields: [
                {
                    key: 'sku',
                    label: 'SKU',
                    sortable: true,
                    automaticSearch: true
                },
                {
                    key: 'termName',
                    label: 'Term',
                    sortable: true
                },
                {
                    label: 'Author',
                    key: 'author',
                    sortable: true,
                    automaticSearch: true,
                    hidden:true
                },
                {
                    label: 'Title',
                    key: 'title',
                    sortable: true,
                    automaticSearch: true,
                    hidden:true
                },
                {
                    label: 'Description',
                    key: 'authorTitleOrDescription',
                    sortable: true,
                    automaticSearch: true,
                    hidden:true
                },
                {
                    label: 'Author /Title or Description',
                    key: 'authorTitleOrDescription',
                    sortable: true,
                },
                {
                    key: 'productType',
                    label: 'Product Type'
                },
                {
                    key: 'copyright',
                    label: 'Variant 1',
                    sortable: true,
                    // GM products sort by the variant's configured sort order; books (no variant
                    // sort order) fall back to the displayed Copyright text. Sort order is zero-padded
                    // so it compares numerically (otherwise "10" would sort before "2").
                    sortByFormatted: (value, key, item) => item.variant1SortOrder != null ? String(item.variant1SortOrder).padStart(10, '0') : (value ?? '')
                },
                {
                    key: 'edition',
                    label: 'Variant 2',
                    sortable: true,
                    sortByFormatted: (value, key, item) => item.variant2SortOrder != null ? String(item.variant2SortOrder).padStart(10, '0') : (value ?? '')
                },
                {
                    key: 'isbnCatalog',
                    sortable: true,
                    label: 'Catalog # /ISBN',
                    tdClass: 'noWrap'
                },
                {
                    key: 'catalogNumber',
                    label: 'Catalog #',
                    automaticSearch: true,
                    hidden:true
                },
                {
                    key: 'isbn',
                    label: 'ISBN',
                    automaticSearch: true,
                    hidden:true
                },
                {
                    key: 'cost',
                    thClass: 'text-center'
                },
                {
                    key: 'margin',
                    thClass: 'text-center'
                },
                {
                    key: 'retail',
                    thClass: 'text-center'
                },
                {
                    key: 'unitsPerPackage',
                    thClass: 'text-center',
                    tdClass: 'text-end',
                    sortable: true,
                    label: 'Purchase Unit'
                },
                {
                    key: 'qty',
                    thClass: 'text-center'
                },
                {
                    key: 'requiredQty',
                    thClass: 'text-center',
                    tdClass: 'text-end',
                    label: 'Req Qty',
                    formatter: (value, key, item) => {
                        return item.qty * item.unitsPerPackage;
                    }
                },
                {
                    key: 'total',
                    formatter: (value, key, item) => {
                        return currencyFormatter(item.qty * item.cost);
                    },
                    thClass: 'text-center',
                    tdClass: 'text-end'
                },
                {
                    key: 'delete',
                    label: ''
                },
                {
                    key: 'barcodes',
                    label: 'Barcode',
                    hidden: true,
                    automaticSearch: true
                }
            ],
            SupplierDiscountsFields: [
                {
                    key: 'discountCode',
                    label: 'Code'
                },
                {
                    key: 'lowQty',
                    label: 'Low',
                    formatter: (value, key, item) => {
                        if (item.type == 'Dollar') {
                            return currencyFormatter(item.lowQty);
                        } else {
                            return item.lowQty;
                        }
                    }
                },
                {
                    key: 'highQty',
                    label: 'High',
                    formatter: (value, key, item) => {
                        if (item.type == 'Dollar') {
                            return currencyFormatter(item.highQty);
                        } else {
                            return item.highQty;
                        }
                    }
                },
                {
                    key: 'discount',
                    label: 'Disc.%'
                },
                {
                    key: 'type',
                    label: 'Type'
                },
                {
                    key: 'poQuantity',
                    label: 'PO Qty'
                },
                {
                    key: 'poDiscount',
                    label: 'PO Disc.',
                    formatter: currencyFormatter
                }
            ],
            SupplierDiscounts: []
        };
    },
    watch: {
        // A Proposed PO can't update selling prices, so keep the toggle off
        // (and disabled in the template) — force it false on load and whenever
        // the status returns to Proposed so a stale 'true' is never posted.
        statusProposed: {
            handler(isProposed) {
                if (isProposed) {
                    this.purchaseOrder.updateSkuRetailPrices = false;
                }
            },
            immediate: true
        }
    },
    computed: {
        totalRows() {
            return this.purchaseOrder != null &&
                this.purchaseOrder.purchaseOrderDetails != null
                ? this.purchaseOrder.purchaseOrderDetails.length
                : 0;
        },

        total() {
            let total = this.totalWithDiscount(
                this.purchaseOrder != null &&
                    this.purchaseOrder.purchaseOrderDetails != null &&
                    this.purchaseOrder.purchaseOrderDetails
                        .map(d => d.qty * d.cost)
                        .reduce((a, b) => a + b, 0)
            );
            this.setTotal(total);
            return total;
        },
        getFields() {
            if (this.statusProposed) {
                return this.fields;
            }
            let addFields = [...this.fields];
            let idx = 0;

            //I was unable to make indexof work on addRcv array.
            addFields.forEach((x, index) => {
                if (x.key === 'qty') {
                    idx = index;
                }
            });
            addFields.splice(idx + 1, 0, { key: 'rcv', sortable: false });
            addFields.splice(idx + 2, 0, { key: 'cancelled', sortable: false });
            addFields.splice(idx + 3, 0, { key: 'backOrdered', label: 'Back Ordered', sortable: false });
            addFields.splice(idx + 4, 0, {
                key: 'outQty',
                label: 'Out Qty',
                sortable: true,
                sortByFormatted: true,
                thClass: 'text-center',
                tdClass: 'text-end',
                // Return a string: BVN sorts sortByFormatted values via
                // localeCompare (numeric:true), which throws on a raw number.
                // The string sorts numerically and renders identically.
                formatter: (value, key, item) =>
                { return String((item.qty - item.receivedQty - item.cancelQty) * item.unitsPerPackage) } });

            return addFields;
        },
        statusClosed() {
            return this.purchaseOrder.status === 'Closed';
        },
        statusOpen() {
            return this.purchaseOrder.status === 'Open';
        },
        statusProposed() {
            return this.purchaseOrder.status === 'Proposed';
        },
        // Enabled only when at least one detail has a received qty > 0 (so
        // "Print All Rcv Qty" is disabled when nothing has been received).
        hasReceivedQty() {
            return this.purchaseOrder?.purchaseOrderDetails?.some(x => x.receivedQty > 0) ?? false;
        }
    },
    methods: {
        async onSave() {
            if (!(await this.$refs.purchaseOrderForm.validate())) {
                return;
            }

            if(await this.isPurchaseOrderNumberInUse()) {
                return;
            }

            if (!(await this.confirmUpdateSellingPrices())) {
                return;
            }

            this.$emit('savePO');
        },
        // When the "Update Selling Price(s)" toggle is on, confirm before any
        // save that would push the PO's retail/selling prices onto the SKUs.
        // Returns true to proceed, false if the user cancelled.
        async confirmUpdateSellingPrices() {
            if (!this.purchaseOrder.updateSkuRetailPrices) {
                return true;
            }

            const applyPrices = await this.$bvModal.msgBoxConfirm(
                'Update Selling Price(s) is currently toggled to yes.  Do you wish to update the Retail/Selling price for the SKUs on this Purchase Order?',
                {
                    okTitle: 'Save and Apply Price(s)',
                    cancelTitle: 'Cancel'
                }
            );

            return !!applyPrices;
        },
        async isPurchaseOrderNumberInUse() {
            // Clear any previous duplicate error
            this.duplicatePoNumber = false;            

            if (this.purchaseOrder.poNumber) {
                try {
                    const resp = await axios.get('purchaseorders/purchaseordernumberinuse', {
                        params: 
                        { 
                            purchaseOrderNumber: this.purchaseOrder.poNumber, 
                            purchaseOrderId: this.purchaseOrder.purchaseOrderId 
                        }
                    });

                    if (resp.data) {
                        this.duplicatePoNumber = true;
                        this.$appError(`Purchase order number ${this.purchaseOrder.poNumber} is already in use.`);

                        return true;
                    }
                } catch (error) {
                    this.$appError('Error validating purchase order number.');

                    return true;
                }

                return false;
            }
        },
        viewSupplierDiscounts() {
            axios
                .post('suppliers/getsupplierdiscounts', {
                    SupplierId: this.purchaseOrder.supplierId,
                    PurchaseOrderId: this.purchaseOrder.purchaseOrderId,
                    ProductDetails: this.purchaseOrder.purchaseOrderDetails
                })
                .then(resp => {
                    this.SupplierDiscounts = resp.data;
                });
        },
        getBackorderRules(product)
        {
            return `min_value:0|exceedQty:${product.qty * product.unitsPerPackage},${product.backOrderQty}`
        },
        loadWantList(wantListId) {
            this.$router.push(`/wantlists/${wantListId}`);
        },
        totalWithDiscount(value) {
            let dollarValue = value;
            if (
                this.purchaseOrder.discountAmount != null &&
                this.purchaseOrder.discountAmount !== 0
            ) {
                dollarValue = value - this.purchaseOrder.discountAmount;
            } else if (
                this.purchaseOrder.discountPercent != null &&
                this.purchaseOrder.discountPercent !== 0
            ) {
                dollarValue =
                    value - value * (this.purchaseOrder.discountPercent / 100);
            }

            value = dollarValue < 0 ? 0 : dollarValue;

            return value;
        },
        amountDollarCheck(item) {
            if (item === null) {
                this.purchaseOrder.discountAmount = 0;
            }
        },
        amountDiscountCheck(item) {
            if (item === null) {
                this.purchaseOrder.discountPercent = 0;
            }
        },
        dissallowEmpty(item) {
            if (!item.cancelQty) {
                item.cancelQty = 0;
            }
            if (!item.receivedQty) {
                item.receivedQty = 0;
            }
            if(!item.backOrderQty){
                item.backOrderQty = 0;
            }
        },
        setTotal(total) {
            this.purchaseOrder.total = total;
        },
        addSelectedItem(items) {
            // now I have questions about how we deal with locations...
            // I'd also really like to extract this logic to a class...
            let details = items.map(i => ({
                qty: 0,
                receivedQty: 0,
                cancelQty: 0,
                backOrderQty: 0,
                purchaseOrderId: this.purchaseOrder.purchaseOrderId,
                ...i
            }));

            this.purchaseOrder.purchaseOrderDetails = unionBy(
                this.purchaseOrder.purchaseOrderDetails,
                details,
                x => x.sku + '-' + (x.termId ?? 0)
            );

            this.purchaseOrder.purchaseOrderDetails.forEach(i => {
                i.margin = pricing.computeMargin(i.cost, i.retail)
            });
        },
        deleteDetail(detail) {
            let hasInvoiceDetail = false;
            this.purchaseOrder.invoices.forEach(x => {
                if (
                    x.invoiceDetails.some(
                        p => p.sku === detail.sku && p.termId === detail.termId
                    )
                ) {
                    hasInvoiceDetail = true;
                }
            });
            if (hasInvoiceDetail) {
                this.$appError(
                        'This product cannot be deleted because it has invoices/returns attached to it.'
                    )
                return;
            } else {
                this.$bvModal
                    .msgBoxConfirm(
                        'Are you sure you want to remove this product from the purchase order?'
                    )
                    .then(value => {
                        if (value) {
                            let index = this.purchaseOrder.purchaseOrderDetails.indexOf(
                                detail
                            );
                            if (index !== -1) {
                                this.purchaseOrder.purchaseOrderDetails.splice(
                                    index,
                                    1
                                );
                            }
                        }
                    });
            }
        },
        removeProducts(products){
            this.$bvModal
                .msgBoxConfirm('Are you sure you want to remove the selected product(s) from the purchase order?')
                .then((value) => {
                    if (value) {
                        products.forEach((x) => {
                            let index = this.purchaseOrder.purchaseOrderDetails.indexOf(x);
                            if (index !== -1) {
                                this.purchaseOrder.purchaseOrderDetails.splice(index, 1);
                            }
                        });
                    }
                });
        },
        updateCalculation: function(item) {
            switch (this.calculationOption) {
                case 0:
                    item.cost = pricing.computeCost(item.retail, item.margin);
                    break;
                case 1:
                    item.margin = pricing.computeMargin(item.cost, item.retail);
                    break;
                case 2:
                    item.retail = pricing.computeRetail(
                        item.cost,
                        item.margin,
                        item.retail,
                        this.parameters.roundingTarget
                    );
                    break;
            }
        },
        async changePOStatus(value) {
            const statusFromClosedToOpen = this.purchaseOrder.status === 'Closed' && value !== 'Closed';
            if (!statusFromClosedToOpen && !(await this.$refs.purchaseOrderForm.validate())) {
                this.validationCheck++;
                this.$appError(
                        'Please resolve all validation errors on this Purchase Order before changing status.'
                    )
                return;
            } else if (value === 'Closed' && this.purchaseOrder.purchaseOrderDetails.some(x => x.backOrderQty > 0)) {
                this.validationCheck++;
                this.$appError(
                        'Status cannot be changed to \'Closed\' because there are back ordered products.'
                    )
                return;
            } else if (
                value === 'Closed' &&
                this.purchaseOrder.purchaseOrderDetails.some(
                    x => x.receivedQty + x.cancelQty < x.qty
                )
            ) {
                this.validationCheck++;
                this.$appError(
                        'All products must be received or cancelled before closing this Purchase Order.'
                    )
                return;
            } else if (
                this.purchaseOrder.status === 'Closed' &&
                value === 'Proposed'
            ) {
                this.validationCheck++;
                this.$appError('Status cannot be changed \'Closed\' to Proposed.')
                return;
            } else if (
                this.purchaseOrder.status === 'Open' &&
                value === 'Proposed' &&
                this.purchaseOrder.purchaseOrderDetails.some(x => x.receivedQty > 0)
            ) {
                this.validationCheck++;
                this.$appError('Status cannot be changed to \'Proposed\' because receiving has been done on this Purchase Order.')
                return;
            } else if (
                this.purchaseOrder.status === 'Open' &&
                value === 'Proposed' &&
                this.purchaseOrder.invoices.length > 0
            ) {
                this.validationCheck++;
                this.$appError(
                        'Status cannot be changed to \'Proposed\' because they have at invoice(s)/return(s).'
                    )
                return;
            } else {
                this.purchaseOrder.status = value;
            }

            if (!(await this.confirmUpdateSellingPrices())) {
                return;
            }

            this.$emit('savePO');
        },
        showExportPurchaseOrder() {
            this.$bvModal.show('exportPurchaseOrderModal');
        },
        async exportPurchaseOrder(exportFormat) {
            let apiUrl = '/purchaseorders/printpurchaseorders';

            this.isExporting = true;
            let selectedid = [this.purchaseOrder.purchaseOrderId];
            axios
                .post(
                    apiUrl,
                    {
                        ids: selectedid,
                        includeRetail: this.includeRetail,
                        exportFormat: exportFormat,
                        sortBy: this.filters.sortBy,
                        sortDesc: this.filters.sortDesc
                    },
                    { responseType: 'blob' }
                )
                .then(result => {
                    downloadFileToBrowser(
                        result.data,
                        exportFormat == 'pdf' ? `Purchase Order - ${this.purchaseOrder.poNumber}` : `Purchase Order - ${this.purchaseOrder.poNumber}.csv`,
                        exportFormat == 'pdf' ? 'application/pdf' : 'application/csv'
                    );
                    this.$appSuccess(
                            `Purchase Order report generated successfully.`
                        )
                })
                .finally(() => {
                    this.isExporting = false;
                });
        },
        async exportReceivingDocument(exportFormat) {
            let apiUrl = '/purchaseorders/exportpdfreceivingdocument';

            this.isExporting = true;
            let selectedid = [this.purchaseOrder.purchaseOrderId];
            axios
                .post(
                    apiUrl,
                    {
                        ids: selectedid,
                        exportFormat: exportFormat
                    },
                    { responseType: 'blob' }
                )
                .then(result => {
                    downloadFileToBrowser(
                        result.data,
                        exportFormat == 'pdf' ? `Receiving Document - ${this.purchaseOrder.poNumber}` : `Receiving Document - ${this.purchaseOrder.poNumber}.csv`,
                        exportFormat == 'pdf' ? 'application/pdf' : 'application/csv'
                    );
                    this.$appSuccess(
                            `Receiving Document report generated successfully.`
                        )
                })
                .finally(() => {
                    this.isExporting = false;
                });
        },
        async printLabels(printAll) {
            if (!this.purchaseOrder.purchaseOrderId || this.formIsDirty) {
                this.$bvModal.msgBoxOk('Please save the purchase order before printing labels.')
                return;
            }
            
            var locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();

            // If printAll is selected we print all products with qty of 1 regardess of qty entered on the PO
            // If not printAll we only print products with a qty greater than 0 entered on the PO
            let printProducts = this.purchaseOrder.purchaseOrderDetails      
                .map(x => ({
                    sku: x.sku,
                    qty: printAll ? x.receivedQty : 1,
                    defaultBarcodeId: x.defaultBarcodeId,
                }))
                .filter(x => printAll ? (x.qty != null && x.qty != 0) : true);
            
            await axios
                .post('products/printpricetags', {
                    products: printProducts,
                    locationId: locationId
                })
                .then(result => {
                    this.tagContent = result.data;
                    
                    this.$nextTick(() => {
                        printJS('tagContent', 'html', '#tagContent: visible;');
                        this.tagContent = null;
                    });
                });
        },
        batchReceive() {
            this.purchaseOrder.purchaseOrderDetails.forEach(x => {
                x.receivedQty = (x.qty - x.cancelQty - x.backOrderQty);
            });
            this.closePurchaseOrder();
        },
        batchCancel() {
            this.purchaseOrder.purchaseOrderDetails.forEach(x => {
                x.cancelQty = (x.qty - x.receivedQty - x.backOrderQty);
            });
            this.closePurchaseOrder();
        },
        async closePurchaseOrder() {
            if (this.purchaseOrder.purchaseOrderDetails.some(x => x.backOrderQty > 0)) {
                return;
            }

            if (!(await this.confirmUpdateSellingPrices())) {
                return;
            }

            this.purchaseOrder.status = 'Closed';
            this.$emit('savePO');
        },
        async supplierChanged(supplierId)
        {
            if (!supplierId) return;
            let selectedLocationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
            axios
                .get('suppliers', { params: { id: supplierId, locationId: selectedLocationId } })
                .then(resp => {
                    if (resp.data != '') {
                        let supplier = resp.data;
                        this.purchaseOrder.shippingLevelId = supplier.supplierLocation.shippingLevelId;
                        this.purchaseOrder.shippingProviderId = supplier.supplierLocation.shippingProviderId;
                    }
                });
        },
        sortByUpdate(value) {
            this.filters.sortBy = value;
        },
        sortDescUpdate(value) {
            this.filters.sortDesc = value;
        },
    },
    mounted: function() {
        axios.get('products/gmparameters').then(resp => {
            this.parameters = resp.data;
        });
        setTimeout(() => {
            this.afterLoad = true;
        }, 300);
    },
    components: {
        ProductMultiSelectQuickSearch
    }
};
</script>

<style scoped lang="scss">
:deep(.noWrap ) {
    white-space: nowrap;
}
:deep(.form-control.is-invalid ) {
    border-color:#f05050 !important;
}
</style>

<!-- Unscoped: the Add Product(s) modal is teleported to body, so scoped CSS can't reach it -->
<style>
.modal-dialog.po-add-products-modal {
    max-width: 75vw;
}
</style>
